import { updateOrganization } from '../api/firebase/organizations';

export const ORGANIZATION_PAYMENTS_ENABLED_DEFAULT = false;
export const USER_PAYMENTS_ENABLED_DEFAULT = true;

export const StripeOnboardingStatus = {
  NOT_STARTED: 'notStarted', // No calls to Stripe have been made yet
  STARTED: 'started', // The initial redirect to Stripe has occurred
  FAILED: 'failed', // User went through onboarding, but there was an error
  SUCCEEDED: 'succeeded', // User went through onboarding, but it succeeded
  COMPLETED: 'completed', // After a successful onboarding, the user dismisses the message.
};

export function didStripeOnboardingSucceed(organization) {
  return (
    !!organization.stripe?.accountId &&
    (isStripeOnboardingStatusSetToSucceeded(organization) || isStripeOnboardingStatusSetToCompleted(organization))
  );
}

// Self-serve payout timing: the billing-page nudge (showVerifyForPayout) appears only
// once the org's first invoice has been paid via Stripe — i.e., there's real money
// waiting to be paid out. Finances settings is the exception: it always offers the
// verification request, because invoices are not a prerequisite for Stripe onboarding.
// (This is "paid via Stripe", not the invoiceStatusV2 DEPOSITED payout stage.)
export function hasFirstStripeInvoicePaid(organization) {
  return Boolean(organization?.setup?.firstStripeInvoicePaid);
}

// A provider must be verified by our support team before they can connect a payout
// account. `stripe.paymentsEnabled` is the flag support flips after verifying the
// program (license + program name). Until then we show a "verify with support"
// banner instead of the connect option.
export function isPayoutVerified(organization) {
  return Boolean(organization?.stripe?.paymentsEnabled);
}

// Whether the org is eligible to be prompted to connect a payout account.
//  - OFF (default): today's behavior, gated on paymentsEnabled.
//  - delayed-payout-setup ON: gated on verification. A verified org has already passed
//    the gate, so we never suppress its connect prompt — even if we have no recorded
//    first Stripe payment (e.g. orgs verified under the old up-front flow). Unverified
//    orgs are handled by the verify banner once they have a first Stripe payment, so
//    they are not eligible for the connect prompt here.
function isEligibleForPayoutPrompt(organization, delayedPayoutEnabled) {
  return delayedPayoutEnabled ? isPayoutVerified(organization) : areStripePaymentsEnabledForOrganization(organization);
}

// Show the "verify with our support team" banner: delayed-payout-setup only, once the
// first invoice is paid via Stripe (money is waiting) but the provider isn't verified.
export function showVerifyForPayout(organization, delayedPayoutEnabled = false) {
  if (!delayedPayoutEnabled) return false;
  return hasFirstStripeInvoicePaid(organization) && !isPayoutVerified(organization);
}

// return true if no Stripe account id exists, or if one does, but onboarding status is not 'completed'
export function showSetupPayoutRibbon(organization, delayedPayoutEnabled = false) {
  const hasAccountId = !!organization?.stripe?.accountId;
  const isCompleted = isStripeOnboardingStatusSetToCompleted(organization);

  return (
    isEligibleForPayoutPrompt(organization, delayedPayoutEnabled) && (!hasAccountId || (hasAccountId && !isCompleted))
  );
}

export function showSetupPayoutModal(organization, delayedPayoutEnabled = false) {
  const hasAccountId = Boolean(organization?.stripe?.accountId);
  const isSetupPayoutModalDismissed = Boolean(organization?.stripe?.setupModalDismissed);

  const onboardingStatus = organization?.stripe?.onboardingStatus;
  const isSetupNotStarted = !onboardingStatus || onboardingStatus === StripeOnboardingStatus.NOT_STARTED;

  return (
    isEligibleForPayoutPrompt(organization, delayedPayoutEnabled) &&
    !hasAccountId &&
    !isSetupPayoutModalDismissed &&
    isSetupNotStarted
  );
}

// Finish just means either SUCCEEDED or FAILED, i.e. the full flow happened through Stripe
export function didStripeOnboardingFinish(organization) {
  return isStripeOnboardingStatusSetToFailed(organization) || isStripeOnboardingStatusSetToSucceeded(organization);
}

export function isStripeOnboardingAllowed(organization) {
  return organization?.stripe?.onboardingStatus !== StripeOnboardingStatus.NOT_ALLOWED;
}

export function isStripeOnboardingStatusSetToStarted(organization) {
  return organization?.stripe?.onboardingStatus === StripeOnboardingStatus.STARTED;
}

export function isStripeOnboardingStatusSetToFailed(organization) {
  return organization?.stripe?.onboardingStatus === StripeOnboardingStatus.FAILED;
}

export function isStripeOnboardingStatusSetToSucceeded(organization) {
  return organization?.stripe?.onboardingStatus === StripeOnboardingStatus.SUCCEEDED;
}
export function isStripeOnboardingStatusSetToCompleted(organization) {
  return organization?.stripe?.onboardingStatus === StripeOnboardingStatus.COMPLETED;
}

export function areStripePaymentsEnabledForUser(user) {
  return user?.stripe?.paymentsEnabled ?? false;
}

export function areStripePaymentsEnabledForOrganization(organization) {
  return organization?.stripe?.paymentsEnabled ?? ORGANIZATION_PAYMENTS_ENABLED_DEFAULT;
}
export function updateStripeOnboardingStatus(organizationId, stripeOnboardingStatus) {
  updateOrganization(organizationId, {
    'stripe.onboardingStatus': stripeOnboardingStatus,
  });
}

export async function dismissSetupPayoutModalFlag(organizationId) {
  await updateOrganization(organizationId, {
    'stripe.setupModalDismissed': true,
  });
}
